import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Loader2, ChevronRight, Search, GitBranch, Layers, Package, FileCheck, FileX } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STATUS_CFG = {
  UNALLOCATED: { color: '#ef4444', label: 'Unalloc' },
  PARTIAL_HARD: { color: '#f59e0b', label: 'Partial' },
  FULLY_ALLOCATED: { color: '#22c55e', label: 'Full' },
};

const STAGE_CFG = {
  NOT_STARTED: { color: '#94a3b8', label: 'Not Started' },
  FIT_UP: { color: '#3b82f6', label: 'Fit-Up' },
  WELDING: { color: '#f59e0b', label: 'Welding' },
  NDT: { color: '#143A6B', label: 'NDT' },
  PAINTING: { color: '#ec4899', label: 'Paint' },
  DISPATCHED: { color: '#22c55e', label: 'Dispatched' },
  DISPATCH: { color: '#22c55e', label: 'Dispatch' },
};

/* ─── Connector components with fixed pixel offsets ─── */
const AREA_CENTER = 20;
const LINE_CENTER = 18;
const SPOOL_CENTER = 14;

function VLine({ isFirst, isLast, color = 'var(--border-color)', center = 18 }) {
  return (
    <div className="flex-shrink-0 self-stretch relative" style={{ width: 1 }}>
      <div className="absolute left-0" style={{
        top: isFirst ? center : 0,
        bottom: isLast ? `calc(100% - ${center}px)` : 0,
        width: 1, background: color
      }} />
    </div>
  );
}

function HLine({ color = 'var(--border-color)', center = 18 }) {
  return (
    <div className="w-6 flex-shrink-0 self-stretch relative">
      <div className="absolute left-0 right-0" style={{ top: center, height: 1, background: color }} />
    </div>
  );
}

/* ─── Connector from parent to children block ─── */
function ParentConn({ color = 'var(--border-color)', parentCenter = 20 }) {
  return (
    <div className="w-6 flex-shrink-0 self-stretch relative">
      <div className="absolute left-0 right-0" style={{ top: parentCenter, height: 1, background: color }} />
    </div>
  );
}

/* ─── Leaf node: Spool ─── */
function SpoolNode({ spool }) {
  const pct = spool.overallAllocatedPercent || 0;
  const issued = !!spool.materialIssueRequestNumber;
  const stage = spool.currentStage || 'NOT_STARTED';
  const stCfg = STAGE_CFG[stage] || STAGE_CFG.NOT_STARTED;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded border transition-all hover:brightness-95 whitespace-nowrap"
      style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)', borderLeft: `2px solid ${pct >= 100 ? '#22c55e' : pct > 0 ? '#f59e0b' : '#ef4444'}` }}
      data-testid={`tree-spool-${spool.spoolNumber}`}>
      <Package className="w-3 h-3 flex-shrink-0" style={{ color: '#143A6B' }} />
      <span className="text-[9px] font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{spool.spoolNumber}</span>
      <span className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{(spool.spoolInchDia || 0).toFixed(0)}ID</span>
      {issued ? <FileCheck className="w-2.5 h-2.5" style={{ color: '#22c55e' }} /> : <FileX className="w-2.5 h-2.5 opacity-30" style={{ color: '#ef4444' }} />}
      <span className="text-[7px] px-1 py-0.5 rounded-full" style={{ background: stCfg.color + '20', color: stCfg.color }}>{stCfg.label}</span>
    </div>
  );
}

/* ─── Line node with expandable spools ─── */
function LineNode({ line, projectId, isFirst, isLast, isExpanded, onToggle }) {
  const [spools, setSpools] = useState(null);
  const [loading, setLoading] = useState(false);
  const hardPct = line.hardAllocation?.allocationPercent || 0;
  const fabPct = line.fabricationProgress?.overallFabProgressPct || 0;
  const wf = line.workfrontStatus || 'UNALLOCATED';
  const stCfg = STATUS_CFG[wf] || STATUS_CFG.UNALLOCATED;

  useEffect(() => {
    if (isExpanded && !spools) {
      setLoading(true);
      api.get(`/v1/projects/${projectId}/pwm/line-detail`, { params: { lineId: line.lineId } })
        .then(r => setSpools(r.data?.spools || []))
        .catch(() => setSpools([]))
        .finally(() => setLoading(false));
    }
  }, [isExpanded, projectId, line.lineId, spools]);

  return (
    <div className="flex items-stretch" data-testid={`tree-line-${line.lineId}`}>
      <VLine isFirst={isFirst} isLast={isLast} color={stCfg.color + '60'} center={LINE_CENTER} />
      <HLine color={stCfg.color + '60'} center={LINE_CENTER} />
      <div className="flex flex-col">
        {/* Line card */}
        <button onClick={onToggle} className="flex items-center gap-1.5 px-2.5 py-1.5 my-0.5 rounded-lg border transition-all hover:brightness-95 whitespace-nowrap flex-shrink-0"
          style={{ background: isExpanded ? `${stCfg.color}08` : 'var(--card-bg)', borderColor: isExpanded ? stCfg.color : 'var(--border-color)', borderLeft: `3px solid ${stCfg.color}` }}>
          <ChevronRight className="w-3 h-3 flex-shrink-0 transition-transform" style={{ color: stCfg.color, transform: isExpanded ? 'rotate(90deg)' : 'none' }} />
          <Layers className="w-3 h-3 flex-shrink-0" style={{ color: '#143A6B' }} />
          <span className="text-[9px] font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{line.lineId}</span>
          <span className="text-[7px] px-1 rounded" style={{ background: line.agUg === 'AG' ? '#3b82f620' : '#f59e0b20', color: line.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{line.agUg}</span>
          <span className="text-[8px] font-mono" style={{ color: hardPct >= 100 ? '#22c55e' : '#f59e0b' }}>{hardPct.toFixed(0)}%</span>
          {fabPct > 0 && <span className="text-[7px] font-mono" style={{ color: '#3b82f6' }}>F:{fabPct.toFixed(0)}%</span>}
          <span className="text-[7px] px-1 py-0.5 rounded-full" style={{ background: stCfg.color + '20', color: stCfg.color }}>{stCfg.label}</span>
        </button>

        {/* Spools */}
        {isExpanded && (
          <div className="flex items-start ml-2">
            <ParentConn color="#143A6B40" parentCenter={SPOOL_CENTER} />
            <div className="flex flex-col">
              {loading ? (
                <div className="flex items-center gap-1 py-2 px-3">
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--text-muted)' }} />
                  <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Loading...</span>
                </div>
              ) : spools && spools.length > 0 ? (
                spools.map((sp, idx) => (
                  <div key={sp.spoolNumber} className="flex items-stretch">
                    <VLine isFirst={idx === 0} isLast={idx === spools.length - 1} color="#143A6B40" center={SPOOL_CENTER} />
                    <HLine color="#143A6B40" center={SPOOL_CENTER} />
                    <div className="my-0.5"><SpoolNode spool={sp} /></div>
                  </div>
                ))
              ) : (
                <span className="text-[9px] py-1 px-3" style={{ color: 'var(--text-muted)' }}>No spools</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Area node (group of lines) ─── */
function AreaNode({ area, group, projectId, isFirst, isLast, isExpanded, onToggle, expandedLines, toggleLine }) {
  return (
    <div className="flex items-stretch">
      <VLine isFirst={isFirst} isLast={isLast} color="#3b82f640" center={AREA_CENTER} />
      <HLine color="#3b82f640" center={AREA_CENTER} />
      <div className="flex flex-col">
        {/* Area card */}
        <button onClick={onToggle} className="flex items-center gap-1.5 px-3 py-2 my-0.5 rounded-lg border transition-all hover:brightness-95 whitespace-nowrap flex-shrink-0"
          style={{ background: isExpanded ? '#3b82f608' : 'var(--card-bg)', borderColor: isExpanded ? '#3b82f6' : 'var(--border-color)', borderLeft: '3px solid #3b82f6' }}
          data-testid={`tree-area-${area}`}>
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 transition-transform" style={{ color: '#3b82f6', transform: isExpanded ? 'rotate(90deg)' : 'none' }} />
          <span className="text-[10px] font-bold" style={{ color: '#3b82f6' }}>{area}</span>
          <span className="text-[8px] px-1.5 py-0.5 rounded" style={{ background: '#3b82f610', color: '#3b82f6' }}>{group.lines.length}</span>
          <span className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{group.totalId.toFixed(0)}ID</span>
        </button>

        {/* Lines */}
        {isExpanded && (
          <div className="flex items-start ml-2">
            <ParentConn color="#3b82f640" parentCenter={LINE_CENTER} />
            <div className="flex flex-col">
              {group.lines.map((line, lIdx) => (
                <LineNode key={line.lineId} line={line} projectId={projectId}
                  isFirst={lIdx === 0} isLast={lIdx === group.lines.length - 1}
                  isExpanded={expandedLines.has(line.lineId)}
                  onToggle={() => toggleLine(line.lineId)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
export default function PWMDecompositionTree({ projectId, filters }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedAreas, setExpandedAreas] = useState(new Set());
  const [expandedLines, setExpandedLines] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '2000' });
      if (filters?.agUg) params.set('agUg', filters.agUg);
      if (filters?.priority) params.set('priority', filters.priority);
      if (filters?.area) params.set('area', filters.area);
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/v1/projects/${projectId}/pwm/lines?${params}`);
      setLines(res.data?.lines || []);
    } catch { setLines([]); }
    setLoading(false);
  }, [projectId, filters, search, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const areaGroups = useMemo(() => {
    const groups = {};
    lines.forEach(l => {
      const area = l.area || 'Unassigned';
      if (!groups[area]) groups[area] = { lines: [], totalId: 0 };
      groups[area].lines.push(l);
      groups[area].totalId += l.totalLineInchDia || 0;
    });
    return groups;
  }, [lines]);

  const areas = Object.keys(areaGroups).sort();
  const toggleArea = (area) => setExpandedAreas(prev => { const n = new Set(prev); n.has(area) ? n.delete(area) : n.add(area); return n; });
  const toggleLine = (lineId) => setExpandedLines(prev => { const n = new Set(prev); n.has(lineId) ? n.delete(lineId) : n.add(lineId); return n; });
  const expandAll = () => setExpandedAreas(new Set(areas));
  const collapseAll = () => { setExpandedAreas(new Set()); setExpandedLines(new Set()); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  return (
    <div className="space-y-3" data-testid="pwm-decomposition-tree">
      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border flex-1 max-w-xs" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)' }}>
          <Search className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search line ID..."
            className="bg-transparent border-none outline-none text-[11px] w-full" style={{ color: 'var(--text-primary)' }} data-testid="tree-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-[10px] px-2 py-1.5 rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="tree-status-filter">
          <option value="">All Status</option>
          <option value="UNALLOCATED">Unallocated</option>
          <option value="PARTIAL_HARD">Partial Hard</option>
          <option value="FULLY_ALLOCATED">Fully Allocated</option>
        </select>
        <Button size="sm" variant="outline" onClick={expandAll} className="text-[9px] h-6" data-testid="tree-expand-all">Expand All</Button>
        <Button size="sm" variant="outline" onClick={collapseAll} className="text-[9px] h-6" data-testid="tree-collapse-all">Collapse All</Button>
        <div className="ml-auto text-[9px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <GitBranch className="w-3 h-3" /> {areas.length} areas, {lines.length} lines
        </div>
      </div>

      {/* Horizontal Tree */}
      <div className="rounded-lg border overflow-x-auto" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }} data-testid="tree-root">
        <div className="p-4 flex items-start min-w-max">
          {/* Root node */}
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg flex-shrink-0"
            style={{ background: '#143A6B10', border: '1px solid #143A6B30' }}>
            <GitBranch className="w-4 h-4" style={{ color: '#143A6B' }} />
            <div>
              <div className="text-[11px] font-bold" style={{ color: '#143A6B' }}>Piping Workfront</div>
              <div className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{lines.length} lines | {areas.length} areas</div>
            </div>
          </div>

          {areas.length > 0 && (
            <div className="flex items-start">
              <ParentConn color="#143A6B40" parentCenter={AREA_CENTER} />
              <div className="flex flex-col">
                {areas.map((area, aIdx) => (
                  <AreaNode key={area} area={area} group={areaGroups[area]} projectId={projectId}
                    isFirst={aIdx === 0} isLast={aIdx === areas.length - 1}
                    isExpanded={expandedAreas.has(area)} onToggle={() => toggleArea(area)}
                    expandedLines={expandedLines} toggleLine={toggleLine} />
                ))}
              </div>
            </div>
          )}

          {areas.length === 0 && (
            <div className="flex items-center ml-4">
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>No data. Seed from Data Import tab.</span>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[9px] px-1" style={{ color: 'var(--text-muted)' }}>
        {Object.entries(STATUS_CFG).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ background: v.color }} />
            <span>{v.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1"><FileCheck className="w-2.5 h-2.5" style={{ color: '#22c55e' }} /><span>Issued</span></div>
        <div className="flex items-center gap-1"><FileX className="w-2.5 h-2.5 opacity-40" style={{ color: '#ef4444' }} /><span>Not Issued</span></div>
      </div>
    </div>
  );
}
